// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens + glass-card chrome, status Chip mapped to the
// Genesis status-badge color table. Data layer (TIER_PRESETS, mutations,
// server-authoritative validation) untouched.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as pb from '@/generated/console_pb'
import {
  assignLicense,
  issueLicense,
  listLicenses,
  resumeLicense,
  revokeLicense,
  suspendLicense,
} from '@/lib/grpcweb/console-api'
import {
  billingStateLabel,
  deploymentTypeLabel,
  licenseStatusLabel,
  licenseTierLabel,
} from '@/lib/formatters'
import { useAuth } from '@/lib/auth/auth-context'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DANGER, DARK, GREY_300, GREY_400, GREY_500, SUCCESS, WARNING, WHITE } from '@/lib/genesis'

// Tier presets are client-side UX convenience ONLY -- they prefill the
// issue form's dimension fields to match ADR-118 D4's documented tier
// table, but every field remains editable and the SERVER re-validates/
// authoritatively decides what's issuable. This mirrors the task brief's
// explicit instruction: "the SERVER is authoritative — presets just
// prefill defaults, they don't hardcode validation."
const TIER_PRESETS: Record<
  string,
  {
    tier: pb.LicenseTier
    deploymentType: pb.DeploymentType
    connectivityMode: pb.ConnectivityMode
    telemetryRequired: boolean
    billingModel: pb.BillingModel
    seatLimit: number
  }
> = {
  tier1: {
    tier: pb.LicenseTier.LICENSE_TIER_TIER1,
    deploymentType: pb.DeploymentType.DEPLOYMENT_TYPE_HOSTED,
    connectivityMode: pb.ConnectivityMode.CONNECTIVITY_MODE_CONNECTED,
    telemetryRequired: false,
    billingModel: pb.BillingModel.BILLING_MODEL_FLAT,
    seatLimit: 1,
  },
  tier2: {
    tier: pb.LicenseTier.LICENSE_TIER_TIER2,
    deploymentType: pb.DeploymentType.DEPLOYMENT_TYPE_HOSTED,
    connectivityMode: pb.ConnectivityMode.CONNECTIVITY_MODE_CONNECTED,
    telemetryRequired: false,
    billingModel: pb.BillingModel.BILLING_MODEL_FLAT,
    seatLimit: 0,
  },
  tier3: {
    tier: pb.LicenseTier.LICENSE_TIER_TIER3,
    deploymentType: pb.DeploymentType.DEPLOYMENT_TYPE_PRIVATE_CONNECTED,
    connectivityMode: pb.ConnectivityMode.CONNECTIVITY_MODE_CONNECTED,
    telemetryRequired: true,
    billingModel: pb.BillingModel.BILLING_MODEL_PER_SEAT,
    seatLimit: 5,
  },
  tier4: {
    tier: pb.LicenseTier.LICENSE_TIER_TIER4,
    deploymentType: pb.DeploymentType.DEPLOYMENT_TYPE_PRIVATE_OFFLINE,
    connectivityMode: pb.ConnectivityMode.CONNECTIVITY_MODE_OFFLINE,
    telemetryRequired: false,
    billingModel: pb.BillingModel.BILLING_MODEL_CONTRACT,
    seatLimit: 0,
  },
  internal: {
    tier: pb.LicenseTier.LICENSE_TIER_INTERNAL,
    deploymentType: pb.DeploymentType.DEPLOYMENT_TYPE_PRIVATE_CONNECTED,
    connectivityMode: pb.ConnectivityMode.CONNECTIVITY_MODE_CONNECTED,
    telemetryRequired: true,
    billingModel: pb.BillingModel.BILLING_MODEL_NONE,
    seatLimit: 0,
  },
}

export function LicensesPage() {
  const { isAdmin } = useAuth()
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['licenses'],
    queryFn: () => listLicenses({}),
  })

  const [accountId, setAccountId] = useState('')
  const [presetKey, setPresetKey] = useState<keyof typeof TIER_PRESETS>('tier3')
  const [seatLimit, setSeatLimit] = useState(TIER_PRESETS.tier3.seatLimit)
  const [formError, setFormError] = useState<string | null>(null)

  const issueMutation = useMutation({
    mutationFn: () => {
      const preset = TIER_PRESETS[presetKey]
      return issueLicense({
        accountId,
        deploymentType: preset.deploymentType,
        seatLimit,
        connectivityMode: preset.connectivityMode,
        telemetryRequired: preset.telemetryRequired,
        billingModel: preset.billingModel,
        billingState: pb.BillingState.BILLING_STATE_ACTIVE,
        tier: preset.tier,
        idempotencyKey: crypto.randomUUID(),
      })
    },
    onSuccess: () => {
      setAccountId('')
      setFormError(null)
      queryClient.invalidateQueries({ queryKey: ['licenses'] })
    },
    onError: () => setFormError('Failed to issue license.'),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['licenses'] })

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Licenses</h1>

      {isAdmin && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            issueMutation.mutate()
          }}
          className="glass-card mb-6 flex flex-wrap items-end gap-3 p-4"
        >
          <div>
            <label htmlFor="license-account-id" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
              Account ID
            </label>
            <input
              id="license-account-id"
              required
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="glass-input px-2 py-1.5 text-sm"
              style={{ color: WHITE }}
            />
          </div>
          <div>
            <label htmlFor="license-tier-preset" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
              Tier preset
            </label>
            <select
              id="license-tier-preset"
              value={presetKey}
              onChange={(e) => {
                const key = e.target.value as keyof typeof TIER_PRESETS
                setPresetKey(key)
                setSeatLimit(TIER_PRESETS[key].seatLimit)
              }}
              className="glass-input px-2 py-1.5 text-sm"
              style={{ color: WHITE }}
            >
              <option value="tier1">Tier 1 — hosted single-seat</option>
              <option value="tier2">Tier 2 — hosted multi-seat</option>
              <option value="tier3">Tier 3 — private-cloud connected</option>
              <option value="tier4">Tier 4 — on-prem</option>
              <option value="internal">Internal — $0 comp</option>
            </select>
          </div>
          <div>
            <label htmlFor="license-seat-limit" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
              Seat limit (0 = unlimited)
            </label>
            <input
              id="license-seat-limit"
              type="number"
              min={0}
              value={seatLimit}
              onChange={(e) => setSeatLimit(Number(e.target.value))}
              className="glass-input w-28 px-2 py-1.5 text-sm"
              style={{ color: WHITE }}
            />
          </div>
          <button
            type="submit"
            disabled={issueMutation.isPending}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#D4B85C', color: '#18171A', height: '32px' }}
          >
            {issueMutation.isPending ? 'Issuing…' : 'Issue license'}
          </button>
          {formError && <p className="text-xs" style={{ color: WHITE }}>{formError}</p>}
        </form>
      )}

      {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading licenses…</p>}
      {isError && (
        <div className="px-3 py-2" style={{ backgroundColor: DARK, borderLeft: `3px solid ${DANGER}`, borderRadius: '8px' }}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load licenses.</p>
        </div>
      )}
      {!isLoading && !isError && data && data.getLicensesList().length === 0 && (
        <p className="text-sm" style={{ color: GREY_400 }}>No licenses issued yet.</p>
      )}

      {!isLoading && !isError && data && data.getLicensesList().length > 0 && (
        <div className="flex flex-col gap-3">
          {data.getLicensesList().map((lic) => {
            const status = lic.getStatus()
            const canSuspend = status === pb.LicenseStatus.LICENSE_STATUS_ACTIVE
            const canResume = status === pb.LicenseStatus.LICENSE_STATUS_SUSPENDED
            const canRevoke = status !== pb.LicenseStatus.LICENSE_STATUS_REVOKED

            return (
              <div key={lic.getId()} className="glass-card p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-mono text-xs" style={{ color: GREY_500 }}>{lic.getId()}</span>
                  <div className="flex gap-1.5">
                    <Chip label={licenseTierLabel(lic.getTier())} />
                    <Chip label={billingStateLabel(lic.getBillingState())} />
                    <Chip label={deploymentTypeLabel(lic.getDeploymentType())} />
                    <Chip
                      label={licenseStatusLabel(status)}
                      tone={
                        status === pb.LicenseStatus.LICENSE_STATUS_ACTIVE
                          ? 'green'
                          : status === pb.LicenseStatus.LICENSE_STATUS_SUSPENDED
                            ? 'amber'
                            : status === pb.LicenseStatus.LICENSE_STATUS_REVOKED
                              ? 'red'
                              : 'neutral'
                      }
                    />
                  </div>
                </div>
                <div className="mb-3 text-sm" style={{ color: GREY_400 }}>
                  account: <span className="font-mono">{lic.getAccountId()}</span>
                  {' · '}
                  instance:{' '}
                  <span className="font-mono">{lic.getInstanceId() || 'unassigned'}</span>
                  {' · '}
                  seats: {lic.getSeatLimit() === 0 ? 'unlimited' : lic.getSeatLimit()}
                </div>

                {isAdmin && (
                  <div className="flex flex-wrap gap-2">
                    {!lic.getInstanceId() && (
                      <AssignInline
                        licenseId={lic.getId()}
                        onAssigned={invalidate}
                      />
                    )}

                    <ConfirmDialog
                      trigger={(open) => (
                        <button
                          type="button"
                          disabled={!canSuspend}
                          onClick={open}
                          className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
                          style={{ color: GREY_300 }}
                        >
                          Suspend
                        </button>
                      )}
                      title="Suspend license"
                      description="The instance stops receiving handshake refreshes and degrades within its grace period. This can be reversed with Resume."
                      confirmLabel="Suspend"
                      onConfirm={async () => {
                        await suspendLicense(lic.getId(), 'operator action via Console UI')
                        invalidate()
                      }}
                    />

                    <ConfirmDialog
                      trigger={(open) => (
                        <button
                          type="button"
                          disabled={!canResume}
                          onClick={open}
                          className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
                          style={{ color: GREY_300 }}
                        >
                          Resume
                        </button>
                      )}
                      title="Resume license"
                      description="Restores handshake refresh on the instance's next check-in."
                      confirmLabel="Resume"
                      onConfirm={async () => {
                        await resumeLicense(lic.getId())
                        invalidate()
                      }}
                    />

                    <ConfirmDialog
                      trigger={(open) => (
                        <button
                          type="button"
                          disabled={!canRevoke}
                          onClick={open}
                          className="rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
                          style={{ border: `1px solid ${DANGER}`, color: DANGER }}
                        >
                          Revoke
                        </button>
                      )}
                      title="Revoke license — this is TERMINAL"
                      description="Revoking a license cannot be undone. There is no un-revoke path — issue a brand new license instead. The instance will lose access once its cached token expires."
                      confirmLabel="Revoke permanently"
                      destructive
                      onConfirm={async () => {
                        await revokeLicense(lic.getId(), 'operator action via Console UI')
                        invalidate()
                      }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function AssignInline({
  licenseId,
  onAssigned,
}: {
  licenseId: string
  onAssigned: () => void
}) {
  const [instanceId, setInstanceId] = useState('')
  const mutation = useMutation({
    mutationFn: () => assignLicense(licenseId, instanceId),
    onSuccess: () => {
      setInstanceId('')
      onAssigned()
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (instanceId.trim()) mutation.mutate()
      }}
      className="flex items-center gap-1.5"
    >
      <input
        placeholder="instance id"
        value={instanceId}
        onChange={(e) => setInstanceId(e.target.value)}
        className="glass-input w-40 px-2 py-1 text-xs"
        style={{ color: WHITE }}
      />
      <button
        type="submit"
        disabled={mutation.isPending}
        className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
        style={{ color: GREY_300 }}
      >
        Assign
      </button>
    </form>
  )
}

function Chip({ label, tone = 'neutral' }: { label: string; tone?: 'neutral' | 'green' | 'amber' | 'red' }) {
  const toneStyle: Record<string, React.CSSProperties> = {
    neutral: { backgroundColor: DARK, color: GREY_300 },
    green: { backgroundColor: 'rgba(74, 222, 128, 0.12)', color: SUCCESS },
    amber: { backgroundColor: 'rgba(251, 191, 36, 0.12)', color: WARNING },
    red: { backgroundColor: 'rgba(242, 123, 118, 0.12)', color: DANGER },
  }

  return (
    <span className="rounded-full px-2 py-0.5 text-xs" style={toneStyle[tone]}>
      {label}
    </span>
  )
}
